/**
 * Layer 2 of view-row pagination (design/large-samples.md, "View-row
 * pagination"): the decode walk. Produces view rows by walking surviving
 * events; the skeleton lets the walk seek past collapsed/filter-empty spans
 * (zero reads) and chunk stats let runs coalesce without reading uniform
 * interior chunks.
 */
import type { SequenceReader } from "./chunkStore";
import { FilteredCursor, type OrdinalEvent } from "./cursor";
import type { SkeletonIndex } from "./skeletonIndex";
import type { ChunkedEvent, EventChunkStats, SkeletonSpan } from "./types";

export type ViewRow =
  | {
      kind: "span";
      ordinal: number;
      depth: number;
      spanIdx: number;
      span: SkeletonSpan;
      collapsed: boolean;
    }
  | { kind: "event"; ordinal: number; depth: number; ev: ChunkedEvent }
  | {
      kind: "run";
      ordinal: number;
      depth: number;
      runType: string;
      count: number;
      extent: [number, number];
      sample: ChunkedEvent;
    };

/**
 * Event types whose consecutive same-span occurrences merge into one row.
 * Binding constraint (spec): run membership must be decidable from type +
 * span facts available in chunk stats — never from event payload contents.
 */
export const RUN_TYPES: ReadonlySet<string> = new Set(["sandbox"]);

/**
 * Structural marker types: span begin/end pairs and legacy step begin/end
 * pairs (the skeleton folds steps into span-table entries — one contract,
 * no legacy carve-out). Structure drives the walk and is only conditionally
 * a row, never subject to the user's filter.
 */
export const STRUCTURAL_TYPES: ReadonlySet<string> = new Set([
  "span_begin",
  "span_end",
  "step",
]);

const isSpanBegin = (ev: ChunkedEvent): boolean =>
  ev.event === "span_begin" || (ev.event === "step" && ev.action === "begin");

const isSpanEnd = (ev: ChunkedEvent): boolean =>
  ev.event === "span_end" || (ev.event === "step" && ev.action === "end");

export interface DecodeCtx {
  events: SequenceReader<ChunkedEvent>;
  stats: readonly EventChunkStats[];
  skel: SkeletonIndex;
  isCollapsed: (spanId: string) => boolean;
  /** User event-type filter (structure types are force-included separately). */
  visible: (type: string) => boolean;
}

/**
 * Decode view rows for ordinals `[startOrd, endOrd)`. Rows whose decode
 * unit starts in-range are emitted even if (via seeks or runs) they extend
 * past `endOrd`. `headRunContinues` — the caller detected (from the
 * previous chunk's stats) that `startOrd` begins mid-run; leading run
 * events are consumed rowless (the row belongs to the chunk where the run
 * starts).
 */
export async function decodeRange(
  ctx: DecodeCtx,
  startOrd: number,
  endOrd: number,
  headRunContinues: boolean
): Promise<ViewRow[]> {
  const survives = (type: string) =>
    STRUCTURAL_TYPES.has(type) || RUN_TYPES.has(type) || ctx.visible(type);
  const cursor = new FilteredCursor(ctx.events, ctx.stats, startOrd, survives);
  const rows: ViewRow[] = [];
  const spanIds = ctx.skel.spanIds;

  if (headRunContinues) {
    const head = await cursor.peek();
    if (head && RUN_TYPES.has(head.ev.event)) {
      await takeRun(cursor, head); // consumed, attributed to the prior chunk
    }
  }

  for (;;) {
    const item = await cursor.peek();
    if (!item || item.ordinal >= endOrd) {
      break;
    }
    const { ordinal, ev } = item;

    if (isSpanBegin(ev)) {
      const spanIdx = ctx.skel.spanAtBegin(ordinal);
      const span = spanIdx !== undefined ? ctx.skel.spans[spanIdx] : undefined;
      if (spanIdx === undefined || span === undefined) {
        await cursor.next(); // dissolved leaf-tool span: no row, contents inline
        continue;
      }
      const depth = ctx.skel.spanStackAt(ordinal).length - 1;
      if (!ctx.skel.hasVisibleContents(spanIdx, ctx.visible)) {
        cursor.seek(span.extent[1] + 1); // filterEmpty: free
      } else if (ctx.isCollapsed(span.id)) {
        rows.push({
          kind: "span",
          ordinal,
          depth,
          spanIdx,
          span,
          collapsed: true,
        });
        cursor.seek(span.extent[1] + 1); // collapsed regions are free
      } else {
        rows.push({
          kind: "span",
          ordinal,
          depth,
          spanIdx,
          span,
          collapsed: false,
        });
        await cursor.next();
      }
    } else if (isSpanEnd(ev)) {
      await cursor.next();
    } else if (RUN_TYPES.has(ev.event)) {
      const depth = rowDepth(ctx, ordinal, ev, spanIds);
      const run = await takeRun(cursor, item);
      if (ctx.visible(ev.event)) {
        rows.push({
          kind: "run",
          ordinal,
          depth,
          runType: ev.event,
          count: run.count,
          extent: [ordinal, run.last],
          sample: ev,
        });
      }
    } else {
      rows.push({
        kind: "event",
        ordinal,
        depth: rowDepth(ctx, ordinal, ev, spanIds),
        ev,
      });
      await cursor.next();
    }
  }
  return rows;
}

function rowDepth(
  ctx: DecodeCtx,
  ordinal: number,
  ev: ChunkedEvent,
  structuralIds: ReadonlySet<string>
): number {
  const structural = ctx.skel.spanStackAt(ordinal).length;
  // contents of dissolved leaf-tool spans indent one extra level
  return structural + (ev.span_id && !structuralIds.has(ev.span_id) ? 1 : 0);
}

/**
 * Consume a maximal run of consecutive same-type same-span events. Fast
 * path: a following chunk whose stats say "uniform run type, same span at
 * both edges" is counted from stats and skipped unread — only mixed edge
 * chunks are fetched (reads ∝ rows emitted).
 */
async function takeRun(
  cursor: FilteredCursor,
  first: OrdinalEvent
): Promise<{ count: number; last: number }> {
  const runType = first.ev.event;
  const spanId = first.ev.span_id ?? null;
  let count = 0;
  let last = first.ordinal;

  for (;;) {
    const item = await cursor.peek();
    if (
      !item ||
      item.ev.event !== runType ||
      (item.ev.span_id ?? null) !== spanId
    ) {
      break;
    }
    count += 1;
    last = item.ordinal;
    await cursor.next();

    // at a chunk boundary, swallow uniform chunks from stats alone
    let chunkIdx = cursor.chunkIndexOf(last) + 1;
    for (;;) {
      const stats = cursor.statsFor(chunkIdx);
      if (!stats) {
        break;
      }
      const [lo, hi] = cursor.chunkBounds(chunkIdx);
      if (
        last !== lo - 1 ||
        Object.keys(stats.type_counts).some((type) => type !== runType) ||
        (stats.first.span_id ?? null) !== spanId ||
        (stats.last.span_id ?? null) !== spanId
      ) {
        break;
      }
      count += stats.type_counts[runType] ?? 0;
      last = hi - 1;
      cursor.seek(hi);
      chunkIdx += 1;
    }
  }
  return { count, last };
}
